import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from 'app/store';
import {
  CANCEL_BLOCKED_REASON_CODES,
  MAX_DOWNSELL_OFFERS,
} from 'pages/myplan/partials/plan-cancel-warning-modal/config';
import {
  selectCurrentPlanFamily,
  selectOfferPlanCardPriceCents,
  selectOfferPlanCode,
  selectOfferBillingType,
  selectOfferPrice,
  selectOfferSubscriptionTermName,
} from 'services/offer/offerSelectors';
import { selectIsUserAgent } from 'services/session/sessionSelectors';
import { selectUsesComplimentaryCancelFlow } from 'services/user/userSelectors';
import { hasPendingOrFailedFeatures } from 'utils/plan-features';
import type { RawFeature } from 'utils/plan-features';
import { PLAN_ENTITLEMENT_KEYS } from 'utils/constants';
import {
  VIEW_ONLY_REASON_CODES,
  PROCESSING_REASON_CODES,
  INELIGIBILITY_MESSAGES,
} from 'utils/ineligibilityMessages';
import { filterValidMarketingOffers } from 'utils/marketing-offers';
import { buildFamilyAndIndividualUpgradePools } from 'pages/offers-and-upgrades/partials/plan-details/planCardData';
import type { IAvailableOffer } from 'services/plan/types';
import {
  buildDownsellLadder,
  getValidProducts,
  permitsEntitlementValue,
} from './utils';

const selectPlanState = (state: RootState) => state.plan;
const selectUserState = (state: RootState) => state.user;

export const selectPlanEntitlements = createSelector(
  [selectPlanState],
  (planState) => planState.entitlements ?? {},
);

const selectRawPlanFeatures = createSelector([selectPlanState], (planState) =>
  Array.isArray(planState.rawPlanFeatures) ? planState.rawPlanFeatures : [],
);

/* Angular parity — `apps/igww/src/app/user/user-plan.service.ts:91-96`
 * derives child-feature entitlements by iterating `payload.features`
 * from the `GET /api/client/plans/` response and matching
 * `name.includes(<vendor label>) && childFeature === true`.
 *   • SSN Trace     → `ssn_trace`       (Angular line 92-94)
 *   • 4iQ Dark Web  → `dark_web_child`  (Angular line 94-96)
 * React stores the same raw features array in `plan.rawPlanFeatures`
 * via `getUserPlan()` on session bootstrap, so these gates resolve
 * synchronously off cached state. */
export const selectHasSsnTraceChildPlanFeature = createSelector(
  [selectRawPlanFeatures],
  (features) =>
    features.some(
      (f) =>
        typeof f?.name === 'string' &&
        f.name.includes('SSN Trace') &&
        f?.childFeature === true,
    ),
);

export const selectHasDarkWebChildPlanFeature = createSelector(
  [selectRawPlanFeatures],
  (features) =>
    features.some(
      (f) =>
        typeof f?.name === 'string' &&
        f.name.includes('4iQ Dark Web') &&
        f?.childFeature === true,
    ),
);

/* Same raw-plan-features scan as Dark Web / SSN Trace above —
 * matches an entry whose `name` contains `'Social Media'` and whose
 * `childFeature` flag is true. Gates the Social Media Monitoring row
 * on the child member-protection-details page so a plan without the
 * child add-on hides the row entirely (matching the Dark Web /
 * Synthetic Identity rows' behaviour). */
export const selectHasSocialMediaChildPlanFeature = createSelector(
  [selectRawPlanFeatures],
  (features) =>
    features.some(
      (f) =>
        typeof f?.name === 'string' &&
        f.name.includes('Social Media') &&
        f?.childFeature === true,
    ),
);

export const selectEntitlementsLoaded = createSelector(
  [selectPlanState],
  (planState) => planState.entitlementsLoaded,
);

const selectCustomerCareSupportEntitlement = createSelector(
  [selectPlanEntitlements],
  (entitlements) => {
    const key = PLAN_ENTITLEMENT_KEYS.customerCareSupportMethods;
    return {
      isDeclared: key in entitlements,
      value: entitlements[key] ?? null,
    };
  },
);

/**
 * Single source of truth for "does this member get a phone?". An undeclared entitlement
 * withholds nothing — but only once the plan has loaded, or an unfetched `{}` would read
 * as undeclared and flash the phone in.
 */
export const selectHasPhoneCustomerCareSupport = createSelector(
  [selectCustomerCareSupportEntitlement, selectEntitlementsLoaded],
  (entitlement, isLoaded) =>
    isLoaded && permitsEntitlementValue(entitlement, 'phone'),
);

export const selectAvailableOffers = createSelector(
  [selectPlanState],
  (planState) => planState.availableOffers ?? [],
);

export const selectAvailableOffersLoaded = createSelector(
  [selectPlanState],
  (planState) => planState.availableOffersLoaded,
);

export const selectPlanChangeEligibility = createSelector(
  [selectPlanState],
  (planState) => planState.planChangeEligibility,
);

export const selectPlanChangeReasonCode = createSelector(
  [selectPlanChangeEligibility],
  (eligibility) => eligibility?.reasonCode ?? '',
);

/**
 * Whether the billing-term change is blocked (Angular
 * `billing-term.component.html:189` — `[disabled]="!(eligibility$|async)?.eligible"`).
 *
 * Deliberately a **blanket** `eligible !== true` gate, unlike
 * `selectCancelLinkDisabled`, which is a narrow reason-code list. Angular gates
 * its two controls differently on purpose: `PLAN_INITIAL_ACTIVATION` blocks the
 * term change while leaving Cancel Plan enabled.
 *
 * The `!== null` guard keeps the control **enabled** while eligibility is still
 * unresolved, matching `selectCancelLinkDisabled`'s fail-open posture and
 * Angular's `filter(pce => !!pce)` on the store stream.
 */
export const selectIsPlanChangeBlocked = createSelector(
  [selectPlanChangeEligibility],
  (eligibility) => eligibility !== null && eligibility.eligible !== true,
);

/** Slice-derived load gate for the fetch-once guard on Billing details. */
export const selectPlanChangeEligibilityLoaded = createSelector(
  [selectPlanChangeEligibility],
  (eligibility) => eligibility !== null,
);

// Drives orchestrator routing: true → PartialPlanView, false → FullPlanView
export const selectShowPartialPlan = createSelector(
  [selectUserState],
  (userState) =>
    hasPendingOrFailedFeatures((userState.features ?? []) as RawFeature[]),
);

/* Session-loaded (ctp slice) eligibility — mirrors the plan slice
 * copy that `useOffersAndUpgradesBootstrap` populates on-page, but
 * is already in Redux from `useSessionInitialSetup`. Used by
 * surfaces that mount before the offers page (e.g. the My Account
 * tile gate) and read eligibility to decide visibility. */
const selectCtpEligibility = (state: RootState) => state.ctp.eligibility;
const selectCtpAvailableOffers = (state: RootState) =>
  state.ctp.availableOffers ?? [];
const selectCtpIsAvailableOffersFetched = (state: RootState) =>
  state.ctp.isAvailableOffersFetched;

/**
 * TRUE when a trip to `/my-account/offers-and-upgrades` would land
 * the user on the `NoOffersState` empty page — a dead-end click.
 * Encapsulates BOTH places the offers page renders that empty
 * state so entry-point gates (e.g. the My Account tile) stay in
 * sync with the page without duplicating the condition.
 *
 *   • FullPlanView path (`full-plan-view/index.tsx:52-73`):
 *     `!processing && !showProducts && !errorMessage` — i.e. the
 *     eligibility response is a hard-no (ineligible, no view-only
 *     grace, no mapped error message, not processing).
 *   • PlanDetails path (`plan-details/index.tsx:421-423`):
 *     `!processing && !hasAnyUpgrades` — enters the eligible
 *     branch but after filtering `availableOffers` the family and
 *     individual upgrade pools are both empty.
 *
 * Fail-open:
 *   – PartialPlanView branch (`hasPendingOrFailedFeatures`) always
 *     renders content → never considered "no offers".
 *   – Eligibility not loaded yet → return false so callers keep
 *     their link visible until data lands.
 */
export const selectWillShowNoOffersPage = createSelector(
  [
    selectShowPartialPlan,
    selectCtpEligibility,
    selectCtpAvailableOffers,
    selectCtpIsAvailableOffersFetched,
    selectOfferPlanCode,
    selectOfferBillingType,
    selectOfferPrice,
  ],
  (
    showPartialPlan,
    eligibility,
    availableOffers,
    isAvailableOffersFetched,
    currentPlanCode,
    currentBillingType,
    currentPrice,
  ) => {
    if (showPartialPlan) return false;
    if (!eligibility) return false;

    const reasonCode = eligibility.reasonCode ?? '';
    if (PROCESSING_REASON_CODES.has(reasonCode)) return false;
    if (VIEW_ONLY_REASON_CODES.has(reasonCode)) return false;
    if (INELIGIBILITY_MESSAGES[reasonCode]) return false;

    if (eligibility.eligible !== true) {
      /* FullPlanView NoOffersState path — no mapped error, not
       * view-only, not processing. Independent of the available
       * offers fetch state. */
      return true;
    }

    /* Eligible path: PlanDetails will render NoOffersState if the
     * upgrade pools are empty after filtering. Mirrors the
     * composition inside `plan-details/index.tsx:153-172`.
     *
     * Fail-open on the fetch state: if available offers haven't
     * resolved yet, we don't know whether the pools would be
     * empty, so keep the entry link visible. The flag flips true
     * on any resolution of the fetch (including an empty response
     * that would genuinely trigger the empty state).
     *
     * Cast via `unknown` because `state.ctp.availableOffers` is
     * typed as `IAvaliableOffer` (sic) and the plan-side utilities
     * expect `IAvailableOffer` — the server payload is identical,
     * so this is a type-level rename, not a data risk. */
    if (!isAvailableOffersFetched) return false;

    const offersForFilter = availableOffers as unknown as IAvailableOffer[];
    const validOffers = filterValidMarketingOffers(
      offersForFilter,
      eligibility,
    );
    const { famUpgrades, indUpgrades } = buildFamilyAndIndividualUpgradePools(
      validOffers,
      {
        currentPlanCode,
        currentBillingType,
        currentPrice: currentPrice ?? 0,
      },
    );
    return famUpgrades.length === 0 && indUpgrades.length === 0;
  },
);
// Family toggle is visible when there is at least one family offer available
export const selectIsFamilyToggleVisible = createSelector(
  [selectAvailableOffers],
  (offers) => offers.some((o) => o.plan.family),
);

// Term selector is visible when offers span more than one distinct billing term
export const selectIsTermSelectorVisible = createSelector(
  [selectAvailableOffers],
  (offers) => {
    const terms = new Set(
      offers.map((o) => o.offer.subscriptionPlan?.termName).filter(Boolean),
    );
    return terms.size > 1;
  },
);

export const selectHasMigratedTbcrReport = createSelector(
  [selectPlanEntitlements],
  (entitlements) =>
    entitlements.migrated_tbcr_report != null ||
    entitlements.migrated_credit_report != null,
);

/**
 * The downsell offers to show, in render order (BRD FR-014 / FR-039 / BR-005).
 *
 * Composes the two pure functions in `./utils`: `getValidProducts` builds the
 * candidate pool with identityWeb's four ordered pre-filters, then
 * `buildDownsellLadder` runs the three rungs over a pool pre-sorted
 * price-ascending. Empty means "no offer applies", which is a legitimate outcome
 * and makes the downsell step skip.
 *
 * The current price comes from `selectOfferPlanCardPriceCents`, **not** the raw
 * `selectOfferCustomerPrice`. `offer.customerPrice` is nullable, and every rung
 * now tests `offer.price < currentPrice`, so a null coerced to `0` would make
 * every rung match nothing and take the whole downsell feature dark — no offer
 * for anyone, on any reason. The null-safe selector falls back to `offer.price`,
 * which is non-nullable, and is already what `keep-my-plan/converted-to-paid.tsx`
 * uses for this same field.
 *
 * identityWeb has the raw exposure via `currentPlanCost =
 * product.offer.customerPrice`. That is a latent defect there rather than
 * behaviour worth reproducing: it silently denies a retention offer the member is
 * entitled to, which is the same class of failure BRD FR-045 exists to prevent.
 *
 * **The `MAX_DOWNSELL_OFFERS` cap is applied here, after the ladder**, so that
 * `buildDownsellLadder` stays a faithful port of Angular's *selection* logic and
 * the cap stays a *display* concern. Do not move it into that function. This is
 * also the single choke point every consumer reads through — the gate below, the
 * modal-width test in `plan-cancel-warning-modal`, and both render branches in
 * `downsell-modal` — which is what makes one slice cap web and mobile alike.
 */
export const selectDownsellLadder = createSelector(
  [
    selectAvailableOffers,
    selectOfferPlanCode,
    selectOfferPlanCardPriceCents,
    selectCurrentPlanFamily,
    selectOfferSubscriptionTermName,
  ],
  (availableOffers, planCode, currentPrice, family, termName) =>
    buildDownsellLadder(getValidProducts(availableOffers), {
      /* `planCode` is optional on the offer slice. Empty means rung 1 (same code)
         matches nothing and rung 3 (different code) matches everything — which is
         also what identityWeb does with an undefined `currentPlanCode`. */
      planCode: planCode ?? '',
      customerPrice: currentPrice,
      family,
      termName,
    }).slice(0, MAX_DOWNSELL_OFFERS),
);

/**
 * The store-derived part of the downsell gate (BRD FR-013 / BR-004).
 *
 * BR-004 requires four conditions. Three are here: the member is on the paying
 * cancellation flow, plan change is eligible, and the ladder returned at least one
 * offer.
 *
 * The first of those reads `selectUsesComplimentaryCancelFlow`, not `billable`.
 * That widens the exclusion from complimentary members to **every non-primary
 * holder, billable or not**: a secondary member's membership is paid for by the
 * primary, so a retention offer would be shown to someone who cannot act on it.
 * This selector — not any UI branch — is what guarantees a *paid* secondary member
 * never reaches the downsell step, whatever route or reason they arrive by.
 *
 * The fourth — "the reason is not `TECHNICAL_ISSUES` and not `OTHERS`" — is
 * **deliberately absent**, because the selected reason lives in Formik state, not
 * Redux, so no selector can see it. The orchestrator ANDs that condition in. Do
 * not read this selector as BR-004 in full.
 *
 * Note also what this is *not*: it is not the offers-loaded check. FR-045 / G10
 * require the flow to **block** on `selectAvailableOffersLoaded` and show a
 * pending state, which is different from this gate being false. Folding the two
 * together would recreate identityWeb's silent-skip defect, where a member whose
 * offers had not landed was sent straight to cancellation with no offer shown.
 */
export const selectDownsellGate = createSelector(
  [
    selectUsesComplimentaryCancelFlow,
    selectPlanChangeEligibility,
    selectDownsellLadder,
  ],
  (usesComplimentaryFlow, eligibility, ladder) =>
    !usesComplimentaryFlow &&
    eligibility?.eligible === true &&
    ladder.length > 0,
);

/**
 * Whether the "Cancel membership" entry link renders but is non-interactive
 * (BRD FR-002 / BR-001).
 *
 * This is Angular's rule verbatim — `billing-term.component.html:35`, which gates
 * Cancel Plan on the reason code alone and contains **no `user.status` check**.
 *
 * It previously carried a `status !== 'pending cancellation'` precedence clause,
 * on the strength of BR-024. That clause is gone, and must not come back: BR-024
 * was derived from Angular's `account-cancel-modal.component.ts:76-78`
 * (`if (status === 'pending cancellation') goToStep(2)`) on the assumption it was
 * live behaviour. It is not reachable for the member it targets. The modal's only
 * `dialog.open` in the whole Angular repo is `billing-term.component.ts:245`,
 * behind this very button — and a `pending cancellation` member almost always
 * also carries `reasonCode: USER_CANCELLING`, so the reason-code rule disables
 * the sole door to the status branch. Reading the two rules as coexisting signals
 * is what produced the phantom precedence.
 *
 * Confirmation variant 3b is still retained. It remains the post-DELETE state,
 * and it is still reached by the status deep-link for a `pending cancellation`
 * member whose reason code is *not* blocking — the one combination where
 * Angular's status branch genuinely executes.
 *
 * This selector is only the *disabled* half of the entry-control rule. The
 * *hidden* half — non-primary user type and the agent guard — is
 * `selectCancelLinkHidden`.
 */
export const selectCancelLinkDisabled = createSelector(
  [selectPlanChangeReasonCode],
  (reasonCode) => CANCEL_BLOCKED_REASON_CODES.includes(reasonCode),
);

/**
 * Whether the "Cancel membership" entry control renders at all — the *hidden*
 * half of BR-024, and the highest-precedence rule of the four.
 *
 * **Agent-impersonated session** is now the only reason to hide it (a retained
 * React-only guard, BRD Section 6): a support agent acting as the member must not
 * be able to cancel on their behalf.
 *
 * The former first reason — **non-primary account holder** — is gone. Cancellation
 * is no longer primary-only: a secondary member cancels their own membership
 * through the complimentary flow, which is what
 * `selectUsesComplimentaryCancelFlow` selects for them. That supersedes FR-035 /
 * BR-014 / AC-047. Do not reinstate the `selectIsNonPrimaryUser` input here; the
 * secondary member's card mounts this very control deliberately.
 *
 * What replaces it as the paid-flow guard is `selectDownsellGate`, which excludes
 * every non-primary holder — so unhiding the link cannot leak a retention offer to
 * someone who is not paying for the plan.
 *
 * Hidden is *not* the same as disabled, and the two must never be conflated —
 * hidden means no control exists, disabled means it exists and explains itself.
 */
export const selectCancelLinkHidden = createSelector(
  [selectIsUserAgent],
  (isAgent) => Boolean(isAgent),
);
